"use client";
import React, { useEffect, useRef, memo, useMemo } from "react";
import { cn } from "utils";
import CardLoading from "../Chat/Cards/CardLoading";
import Empty from "../Chat/Charts/Empty";
import { ChartDataType } from "../Chat/Cards/EcosystemDetail";
import { createDualYAxisLineChart } from "@components/createChart";
import {
  MaybeTradingData,
  useStockDetailsMarket,
} from "@hooks/useStockDetailsMarket";
import { useIsPc } from "@hooks/useIsPc";
import { useStockLineChart } from "@hooks/useStockLineChart";
import { PRICE_CHART_QUERY_PARAMS } from "@config/stock";
import { getRegionCurrencySymbol } from "@utils/stock";

export type IntervalType = "1d" | "5d" | "30d";

const intervalMap = {
  "1d": "1m",
  "5d": "15m",
  "30d": "1h",
};

interface ChartContainerProps {
  className?: string;
  selectedDataType?: ChartDataType;
  dataType: keyof typeof intervalMap;
  handleShowChart?: (type: any) => void;
  createdAt?: string;
  setSelectedDataType?: (type: string) => void;
  symbol?: string;
  short_name?: string;
}

const containerClass =
  "w-full max-w-212.5 h-45.5 min-h-45.5 [content-visibility:auto]";

const StockDetailLine: React.FC<ChartContainerProps> = ({
  className = "",
  dataType,
  symbol,
  short_name,
}) => {
  const lineChartRef = useRef<HTMLDivElement>(null);
  const tradingDataRef = useRef<MaybeTradingData>(null);

  const interval = intervalMap[dataType];
  const [isPC] = useIsPc();

  const {
    data: stockData,
    isLoading: freshDataLoading,
    isPending,
  } = useStockDetailsMarket({
    enabled: true,
    symbol,
    queryParams: {
      ...PRICE_CHART_QUERY_PARAMS,
      "enrich.with_sparkline.interval": interval,
      "enrich.with_sparkline.trading_period": dataType,
      "enrich.with_trading_data": !tradingDataRef.current,
    },
    queryKey: ["price-chart", dataType],
  });
  const currentSymbolData = useMemo(() => {
    if (isPending) {
      return;
    }
    const symbolData = stockData;
    // log.debug("[StockDetailLine] currentSymbolData", tradingDataRef.current, stockData?.trading_data);
    if (tradingDataRef.current) {
      return {
        ...stockData,
        trading_data: tradingDataRef.current,
      };
    }
    tradingDataRef.current = symbolData?.trading_data;
    return symbolData;
  }, [stockData, isPending]);
  const { xAxisData, yAxisData, xKeyTicks } = useStockLineChart({
    name: short_name!,
    logo: currentSymbolData?.logo || "/assets/images/image_place_holder.png",
    trading_data: currentSymbolData?.trading_data,
    sparkline: currentSymbolData?.sparkline,
    dataType,
    market: currentSymbolData?.market,
  });
  useEffect(() => {
    if (lineChartRef.current) {
      createDualYAxisLineChart({
        config: {
          el: lineChartRef.current!,
          tooltipValueFormat: (value: MaybeOrNull<number>) => {
            if (value === null) {
              return "";
            }
            return `${getRegionCurrencySymbol(currentSymbolData?.currency)}${value.toLocaleString(
              undefined,
              {
                maximumFractionDigits: 10,
              },
            )}`;
          },
          tooltipFormatter: isPC ? undefined : () => " ",
        },
        XAxis: xAxisData as any,
        YAxis: yAxisData as any,
        ticks: {
          xKeyTicks: xKeyTicks as any,
        } as any,
      });
    }
  }, [xAxisData, yAxisData, xKeyTicks, isPC]);

  // left必须有值，右侧对比值根据check按钮进行选中和取消选中
  if ((!freshDataLoading && !xAxisData?.length) || !yAxisData?.left?.length) {
    return (
      <Empty
        className={containerClass}
        title=""
        description="No Data Yet"
        descriptionClassName="body_medium_m"
        src="/assets/images/chart_no_data.webp"
        hiddenTitle
        imageSize={72}
      />
    );
  }

  if (freshDataLoading) {
    return <CardLoading className={cn(containerClass)} />;
  }
  return (
    <div className={cn(containerClass, className)} ref={lineChartRef}></div>
  );
};

export default memo(StockDetailLine, (prev, next) => {
  return (
    prev.symbol === next.symbol &&
    prev.dataType === next.dataType &&
    prev.className === next.className &&
    prev.short_name === next.short_name
  );
});
